import { CursorArrowRaysIcon } from "@heroicons/react/24/outline";
import { useStore } from "@nanostores/react";
import { ReactElement, useEffect, useState } from "react";

import { GraphNode } from "../../api";
import { colorSchemas } from "../../config/entities";
import { $syncedGraphContent } from "../../stores/graph/content";
import { $cytoscapeInstance } from "../../stores/graph/ui.ts";
import { Button } from "../catalyst/Button";
import { IconRenderer } from "../icons/IconRenderer";
import { Tabs } from "../Tabs";
import { FloatingTool } from "./FloatingTool.tsx";

interface EntityType {
  count: number;
  entityId: string;
  icon?: string;
  name?: string;
}

interface FlagType {
  color: string;
  count: number;
  name: string;
}

export const FloatingNodeSelector = (): ReactElement => {
  const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
  const [flagTypes, setFlagTypes] = useState<FlagType[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const cy = useStore($cytoscapeInstance);
  const syncedGraphContent = useStore($syncedGraphContent);

  useEffect(() => {
    if (!syncedGraphContent?.nodes) return;

    // Build entity type map with counts using denormalized data from nodes
    const typeDataMap = new Map<string, EntityType>();

    syncedGraphContent.nodes.forEach((node: GraphNode) => {
      const entityId = node.entityId;
      if (!entityId) return;

      const existing = typeDataMap.get(entityId);
      if (existing) {
        // Increment count for existing entity type
        existing.count++;
      } else {
        // Add new entity type with denormalized data
        typeDataMap.set(entityId, {
          count: 1,
          entityId,
          icon: node.icon, // From denormalized data
          name: node.entityName, // From denormalized data
        });
      }
    });

    // Convert to array and sort by name
    const result = Array.from(typeDataMap.values()).sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    );

    setEntityTypes(result);
  }, [syncedGraphContent]);

  useEffect(() => {
    if (!syncedGraphContent?.nodes) return;

    // Get flag color counts from the graph
    const flagCounts = new Map<string, number>();
    syncedGraphContent.nodes.forEach((node: GraphNode) => {
      if (node.isFlagged && node.flagColor) {
        flagCounts.set(
          node.flagColor,
          (flagCounts.get(node.flagColor) || 0) + 1,
        );
      }
    });

    // Create flag types from available colors
    const availableFlagTypes = Object.entries(colorSchemas)
      .map(([name, colors]) => ({
        color: colors.selected,
        count: flagCounts.get(colors.selected) || 0,
        name: name.charAt(0).toUpperCase() + name.slice(1),
      }))
      .filter((flagType) => flagType.count > 0) // Only show flags that have nodes
      .sort((a, b) => b.count - a.count); // Sort by count descending

    setFlagTypes(availableFlagTypes);
  }, [syncedGraphContent]);

  const handleNodeTypeSelect = (entityId: string): void => {
    if (!cy) return;

    // Clear previous selection
    cy.elements().unselect();

    // Select all nodes of the chosen type
    cy.nodes()
      .filter((node) => node.data("entityId") === entityId)
      .select();

    // Fit to view if there are selected nodes
    if (cy.nodes(":selected").length > 0) {
      cy.fit(cy.nodes(":selected"));
    }
  };

  const handleFlagSelect = (flagColor: string): void => {
    if (!cy) return;

    // Clear previous selection
    cy.elements().unselect();

    // Select all nodes with the chosen flag color
    cy.nodes()
      .filter(
        (node) =>
          node.data("flagColor") === flagColor && node.data("isFlagged"),
      )
      .select();

    // Fit to view if there are selected nodes
    if (cy.nodes(":selected").length > 0) {
      cy.fit(cy.nodes(":selected"));
    }
  };

  const handleSelectAllNodes = (): void => {
    if (!cy) return;
    cy.elements().unselect();
    cy.nodes().select();
    if (cy.nodes(":selected").length > 0) {
      cy.fit(cy.nodes(":selected"));
    }
  };

  const handleSelectAllFlagged = (): void => {
    if (!cy) return;
    cy.elements().unselect();
    cy.nodes()
      .filter((node) => node.data("isFlagged"))
      .select();
    if (cy.nodes(":selected").length > 0) {
      cy.fit(cy.nodes(":selected"));
    }
  };

  const nodeSelectorIcon = (
    <CursorArrowRaysIcon className="h-6 w-6 cursor-pointer text-zinc-400 hover:text-zinc-200" />
  );

  const renderNodeTypesTab = (): ReactElement => (
    <div className="max-h-[250px] overflow-y-auto">
      <Button
        className="mb-1 w-full font-normal"
        disabled={!syncedGraphContent?.nodes?.length}
        onClick={handleSelectAllNodes}
        plain
        size="sm"
      >
        <div className="grid w-full grid-cols-[1fr_40px] items-center gap-2">
          <div className="flex items-center gap-2">
            <span>Select All</span>
          </div>
          <div className="flex justify-end">
            <span className="rounded-full bg-zinc-700 px-2 py-1 text-xs text-zinc-400">
              {syncedGraphContent?.nodes?.length || 0}
            </span>
          </div>
        </div>
      </Button>
      <div className="-mx-1 mb-1 border-t border-zinc-700" />
      {entityTypes.length === 0 ? (
        <div className="flex items-center gap-2 py-2 text-sm text-zinc-500">
          <span>No nodes in graph</span>
        </div>
      ) : (
        entityTypes.map(({ count, entityId, icon, name }) => (
          <Button
            className="w-full font-normal"
            key={entityId}
            onClick={() => handleNodeTypeSelect(entityId)}
            plain
            size="sm"
          >
            <div className="grid w-full grid-cols-[1fr_40px] items-center gap-2">
              <div className="flex min-w-0 items-center gap-2">
                {icon && <IconRenderer className="h-4 w-4" icon={icon} />}
                <span className="truncate">{name || entityId}</span>
              </div>
              <div className="flex justify-end">
                <span className="rounded-full bg-zinc-700 px-2 py-1 text-xs text-zinc-400">
                  {count}
                </span>
              </div>
            </div>
          </Button>
        ))
      )}
    </div>
  );

  const renderFlagsTab = (): ReactElement => (
    <div className="max-h-[250px] overflow-y-auto">
      <Button
        className="mb-1 w-full font-normal"
        disabled={flagTypes.reduce((acc, f) => acc + f.count, 0) === 0}
        onClick={handleSelectAllFlagged}
        plain
        size="sm"
      >
        <div className="grid w-full grid-cols-[1fr_40px] items-center gap-2">
          <div className="flex items-center gap-2">
            <span>Select All</span>
          </div>
          <div className="flex justify-end">
            <span className="rounded-full bg-zinc-700 px-2 py-1 text-xs text-zinc-400">
              {flagTypes.reduce((acc, f) => acc + f.count, 0)}
            </span>
          </div>
        </div>
      </Button>
      <div className="-mx-1 mb-1 border-t border-zinc-700" />
      {flagTypes.length === 0 ? (
        <div className="flex items-center gap-2 py-2 text-sm text-zinc-500">
          <span>No flagged nodes</span>
        </div>
      ) : (
        flagTypes.map(({ color, count, name }) => (
          <Button
            className="w-full font-normal"
            key={color}
            onClick={() => handleFlagSelect(color)}
            plain
            size="sm"
          >
            <div className="grid w-full grid-cols-[1fr_40px] items-center gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <div
                  className="h-4 w-4 rounded-sm border border-zinc-600"
                  style={{ backgroundColor: color }}
                />
                <span className="truncate">{name}</span>
              </div>
              <div className="flex justify-end">
                <span className="rounded-full bg-zinc-700 px-2 py-1 text-xs text-zinc-400">
                  {count}
                </span>
              </div>
            </div>
          </Button>
        ))
      )}
    </div>
  );

  const nodeSelectorContent = (
    <div className="ring-.75 max-h-[300px] w-fit min-w-[200px] max-w-[60ch] overflow-hidden rounded-lg bg-zinc-900/90 p-3 shadow-lg ring-zinc-600">
      {/* Tab Navigation */}
      <div className="mb-3">
        <Tabs
          currentId={activeTabIndex === 0 ? "nodeTypes" : "flags"}
          items={[
            {
              id: "nodeTypes",
              name: "Type",
            },
            {
              id: "flags",
              name: "Flag",
            },
          ]}
          onChange={(index) => setActiveTabIndex(index)}
        />
      </div>

      {/* Tab Content */}
      <div className="flex flex-col space-y-1">
        {activeTabIndex === 0 ? renderNodeTypesTab() : renderFlagsTab()}
      </div>
    </div>
  );

  return (
    <FloatingTool icon={nodeSelectorIcon}>{nodeSelectorContent}</FloatingTool>
  );
};
